/**
 * PDF Analysis Service
 * API calls for PDF-to-JSON AI analysis module
 */

import axios from '../utils/axios';
import { API_BASE_URL } from '../config/api.config';
import { useAuthStore } from '../store/authStore';
import type {
  PdfAnalysis,
  PdfAnalysisListItem,
  PdfAnalysisTopicSummary,
  PdfAnalysisStatusResponse,
  PdfAnalysisUploadResponse,
  ExtractionMode,
  VersionConfig,
  CreateVersionPayload,
  QuestionVersionTree,
} from '../types/pdf-analysis.types';

const API_URL = '/pdf-to-json';

class PdfAnalysisService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Upload a PDF for AI analysis (returns immediately with 202)
   */
  async analyzePdf(file: File, mode: ExtractionMode = 'auto'): Promise<PdfAnalysisUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/analyze-json?mode=${mode}`,
      formData,
      {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  /**
   * Get processing status of an analysis (for polling)
   */
  async getAnalysisStatus(id: string): Promise<PdfAnalysisStatusResponse> {
    const response = await axios.get(`${API_URL}/analyses/${id}/status`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Get all analyses (list view)
   */
  async getAnalyses(): Promise<PdfAnalysisListItem[]> {
    const response = await axios.get(`${API_URL}/analyses`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data || [];
  }

  /**
   * Get a single analysis by ID (full detail)
   */
  async getAnalysis(id: string): Promise<PdfAnalysis> {
    const response = await axios.get(`${API_URL}/analyses/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Get lightweight topic data for multiple analyses (batch).
   * Returns only oeCode, topics, difficulty, keywords per item.
   */
  async getAnalysesTopics(ids: string[]): Promise<PdfAnalysisTopicSummary[]> {
    const response = await axios.post(
      `${API_URL}/analyses/topics`,
      { ids },
      { headers: this.getAuthHeaders() }
    );
    return response.data.data || [];
  }

  /**
   * Generate questions from PDF analysis data.
   * Uses pre-analyzed topic data + validation config rules via LLM.
   */
  async generateQuestions(params: {
    questionType: string;
    difficultyDistribution: { easy: number; medium: number; hard: number };
    count: number;
    mcqFormat?: string;
    additionalInstructions?: string;
    questionStyle?: string;
    freeformPrompt?: string;
    selectedTopics: Array<{ pdfId: string; oeCode: string; topicIndex: number }>;
  }): Promise<any> {
    // Audit trail: no auth middleware populates req.user on the backend, so the
    // requesting user travels in the body (same pattern as versioning's createdBy)
    const user = useAuthStore.getState().user;
    const response = await axios.post(
      `${API_URL}/generate-questions`,
      { ...params, requestedBy: user?.name || user?.email || undefined },
     // { headers: this.getAuthHeaders(), timeout: 300000 } // 5 min timeout
    );
    return response.data;
  }

  /**
   * Async generation (client timeout fix): enqueue the batch and poll the
   * job for live progress — no HTTP timeout regardless of batch size.
   */
  async generateQuestionsAsync(params: {
    questionType: string;
    difficultyDistribution: { easy: number; medium: number; hard: number };
    count: number;
    mcqFormat?: string;
    additionalInstructions?: string;
    questionStyle?: string;
    freeformPrompt?: string;
    selectedTopics: Array<{ pdfId: string; oeCode: string; topicIndex: number }>;
  }): Promise<{ jobId: string }> {
    const user = useAuthStore.getState().user;
    const response = await axios.post(`${API_URL}/generate-questions/async`, {
      ...params,
      requestedBy: user?.name || user?.email || undefined,
    });
    return response.data.data;
  }

  async getGenerationJob(jobId: string): Promise<{
    jobId: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress: { total: number; completed: number };
    result?: { questions: any[]; summary: any; failureReasons: string[] };
    error?: string;
  }> {
    const response = await axios.get(`${API_URL}/generate-questions/jobs/${jobId}`);
    return response.data.data;
  }

  /**
   * Generate questions using a custom freeform prompt.
   * Calls the dedicated custom-prompt endpoint with smart defaults.
   */
  async generateQuestionsCustomPrompt(params: {
    prompt: string;
    selectedTopics: Array<{ pdfId: string; oeCode: string; topicIndex: number }>;
    questionType?: string;
    difficultyDistribution?: { easy: number; medium: number; hard: number };
    count?: number;
    mcqFormat?: string;
    additionalInstructions?: string;
    questionStyle?: string;
  }): Promise<any> {
    const user = useAuthStore.getState().user;
    const response = await axios.post(
      `${API_BASE_URL}/pdf-to-json/generate-questions/custom-prompt`,
      { ...params, requestedBy: user?.name || user?.email || undefined },
      { headers: this.getAuthHeaders(), timeout: 300000 }
    );
    return response.data;
  }

  /**
   * Get all PDF-generated questions with optional filters.
   * Reads from pdf_generated_questions collection.
   */
  async getGeneratedQuestions(params?: {
    search?: string;
    difficulty?: string;
    type?: string;
    status?: string;
    oeCode?: string;
    questionId?: string;
    portfolio?: string; // NQG-RQ-29
    taxonomyNode?: string; // NQG-RQ-31: node id, matches at-or-under
    page?: number;
    limit?: number;
  }): Promise<{ questions: any[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const response = await axios.get(`${API_URL}/generated-questions`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data.data;
  }

  /**
   * Distinct source materials for the bank's "filter by source" dropdown
   */
  async getSourceDocuments(): Promise<
    Array<{ filename: string; title?: string; questionCount: number }>
  > {
    const response = await axios.get(`${API_URL}/generated-questions/source-documents`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data || [];
  }
  /**
   * Get a single PDF-generated question by ID.
   */
  async getGeneratedQuestion(id: string): Promise<any> {
    const response = await axios.get(`${API_URL}/generated-questions/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Update a single PDF-generated question by ID.
   */
  async updateGeneratedQuestion(id: string, data: any): Promise<any> {
    const response = await axios.patch(`${API_URL}/generated-questions/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Delete a single PDF-generated question by ID.
   */
  async deleteGeneratedQuestion(id: string): Promise<any> {
    const response = await axios.delete(`${API_URL}/generated-questions/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
  /**
   * Get the configuration-based list of version categories and difficulties
   * (NQG-RQ-27, AC-4 — rendered dynamically, extensible without UI changes).
   */
  async getVersionConfig(): Promise<VersionConfig> {
    const response = await axios.get(`${API_URL}/generated-questions/version-config`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Create a new version of a question (NQG-RQ-27).
   * Generation is an LLM call and can take up to a minute — long timeout.
   */
  async createQuestionVersion(id: string, payload: CreateVersionPayload): Promise<any> {
    const response = await axios.post(
      `${API_URL}/generated-questions/${id}/versions`,
      payload,
      { headers: this.getAuthHeaders(), timeout: 180000 } // 3 min timeout
    );
    return response.data.data;
  }

  /**
   * Get the full version tree + history for a question (NQG-RQ-27).
   * Accepts the id of the original OR any version (resolves to root).
   */
  async getQuestionVersions(id: string): Promise<QuestionVersionTree> {
    const response = await axios.get(`${API_URL}/generated-questions/${id}/versions`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Report AI wrong reasons — upserts by question type into ai_wrong_reports table.
   */
  async reportAiWrong(type: string, aiWrongReasons: string[]): Promise<any> {
    const response = await axios.post(`${API_URL}/ai-wrong-report`, { type, aiWrongReasons }, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Get AI wrong reasons for a question type.
   */
  async getAiWrongReport(type: string): Promise<{ type: string; aiWrongReasons: string[] }> {
    const response = await axios.get(`${API_URL}/ai-wrong-report/${type}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Export generated questions to Excel with filters and ranges
   */
  async exportToExcel(filters: {
    search?: string;
    difficulty?: string;
    type?: string;
    status?: string;
    oeCode?: string;
    fromQuestion?: number;
    toQuestion?: number;
    fromDate?: string;
    toDate?: string;
    validation?: string;
    sourceDocument?: string;
    portfolio?: string; // NQG-RQ-29
    taxonomyNode?: string; // NQG-RQ-31: node id, matches at-or-under
    includeVersions?: string; // NQG-RQ-27: 'true' → versions export alongside originals
    ids?: string; // NQG-RQ-30: comma-separated ids for "export selected"
  } = {}): Promise<Blob> {
    try {
      const response = await axios.get(`${API_URL}/generated-questions/export/excel`, {
        headers: this.getAuthHeaders(),
        params: filters,
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      // When responseType is 'blob', error responses are also blobs
      // We need to parse them to get the actual error message
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          error.response.data = json;
        } catch {
          // If not JSON, keep the text
          error.response.data = { error: text };
        }
      }
      throw error;
    }
  }

  /**
   * Delete a PDF analysis by ID (hard delete from pdf_analyses collection)
   */
  async deleteAnalysis(id: string): Promise<any> {
    const response = await axios.delete(`${API_URL}/analyses/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * Update status for multiple questions (batch operation)
   */
  async batchUpdateQuestionStatus(params: {
    questionIds: string[];
    status: 'approved' | 'rejected';
    rejectionReason?: string;
    updatedBy?: string;
  }): Promise<any> {
    const response = await axios.post(
      `${API_URL}/generated-questions/batch-status`,
      params,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export const pdfAnalysisService = new PdfAnalysisService();
